import { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography, Button, Dialog, IconButton, useTheme, useMediaQuery, Stack } from "@mui/material";
import { submitWeeklyQuizAnswer } from "API/calls";
import BackdropLoading from "components/elements/BackdropLoading";
import { fontWeight } from "config/Theme/options";
import useResponsiveFont from "hooks/useResponsiveFont";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { IWeeklyQuiz } from "types/types";

import QuizConfetti from "./QuizConfetti";
import QuizOption from "./QuizOption";
import QuizResult from "./QuizResult";

export type OptionStatus = "default" | "selected" | "correct" | "incorrect";
export type QuizState = "choosing" | "reviewing" | "correct" | "incorrect";

interface IQuizResult {
  correctAnswerLetter: string;
  xpEarned: number;
  answeredOk: boolean;
}

export default function QuizModal({ onClose, weeklyQuiz }: { onClose: () => void; weeklyQuiz: IWeeklyQuiz }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const { getResponsiveRem } = useResponsiveFont();

  const [isOpenBackdropLoading, setIsOpenBackdropLoading] = useState<boolean>(false);
  const [result, setResult] = useState<IQuizResult | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [quizState, setQuizState] = useState<QuizState>("choosing");
  const isResultState = quizState === "correct" || quizState === "incorrect";

  const handleClose = () => {
    if (quizState === "reviewing") handleSubmit();
    else onClose();
  };

  const handleSubmit = () => {
    if (selected === null) return;

    if (quizState === "choosing") {
      validateQuizAnswer();
    } else if (quizState === "reviewing") {
      setQuizState(result?.answeredOk ? "correct" : "incorrect");
    }
  };

  const getOptionStatus = (key: string): OptionStatus => {
    if (quizState === "choosing") {
      return selected === key ? "selected" : "default";
    }

    if (quizState === "reviewing") {
      if (key === result?.correctAnswerLetter) return "correct";
      if (selected === key) return "incorrect";
      return "default";
    }

    return "default";
  };

  const validateQuizAnswer = async () => {
    if (selected === null) return;
    setIsOpenBackdropLoading(true);

    try {
      const { data } = await submitWeeklyQuizAnswer(selected);
      setResult(data);
      setQuizState("reviewing");
    } catch (error: any) {
      enqueueSnackbar(t("commonError"), { variant: "error" });
    }

    setIsOpenBackdropLoading(false);
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth={isResultState ? "sm" : "md"}
      PaperProps={{ sx: { px: { xs: 3, md: 8 }, pb: { xs: 4, md: 8 }, pt: { xs: 2, md: 4 }, position: "relative" } }}>
      <Box display="flex" justifyContent="flex-end">
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      {(quizState === "choosing" || quizState === "reviewing") && (
        <>
          <Typography fontSize={getResponsiveRem(2)} fontWeight={fontWeight.extraBold} fontFamily={"Nunito"} textAlign={"center"}>
            {t("bitcoinerChallenge.quiz.title", { number: weeklyQuiz.week })}
          </Typography>
          <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
            <Box maxWidth={665} width="100%">
              <Typography fontSize={getResponsiveRem(1.25)} fontWeight={fontWeight.bold} fontFamily={"Nunito"} color="primary">
                {t("bitcoinerChallenge.quiz.question")}
              </Typography>
              <Typography mt={1} mb={4} fontSize={getResponsiveRem(1.25)} fontWeight={fontWeight.bold} fontFamily={"Nunito"}>
                {weeklyQuiz.question}
              </Typography>
              <Stack spacing={1.5}>
                {Object.entries(weeklyQuiz.answers).map(([key, value], idx) => (
                  <QuizOption
                    key={idx}
                    letter={key}
                    text={value}
                    status={getOptionStatus(key)}
                    disabled={quizState !== "choosing"}
                    onClick={() => setSelected(key)}
                  />
                ))}
              </Stack>
              <Box display="flex" justifyContent={{ xs: "center", md: "flex-end" }} mt={{ xs: 4, md: 10 }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{ fontFamily: "Nunito", fontWeight: fontWeight.bold }}
                  disabled={selected === null}
                  onClick={handleSubmit}
                  fullWidth={isMobile}>
                  {quizState === "choosing" ? t("bitcoinerChallenge.quiz.submit") : t("bitcoinerChallenge.quiz.next")}
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
      {isResultState && <QuizResult quizState={quizState} onClose={onClose} earned={result?.xpEarned ?? 0} />}
      <QuizConfetti trigger={quizState === "correct"} />
      <BackdropLoading open={isOpenBackdropLoading} />
    </Dialog>
  );
}
